import { dateFormat, dropDownSet } from "../../../helpers/utilities";
import AppointmentLocationSection from "./appointment-location-section";
import { useAppointmentLocation } from "./use-appointment-location";
import { Stepper, Group, Modal, Badge } from "@mantine/core";
import { DatePicker, Divider, message, Select, TimePicker } from "antd";
import { useState } from "react";
import { getAllNetworkPatients, postNewAppointment } from "../../../helpers/api";
import { getUser } from "../../../helpers/auth";
import NewPatientForm from "../../catalog/components/new-patient";
import { format, isPast, isToday } from "date-fns";
import _ from "lodash";
import { useMutation, useQuery } from "react-query";
import { IconVideo, IconUser } from "@tabler/icons";
import { EditPatientForm } from "../../catalog/components/edit-patient";

const appoinmentState = {
  patientId: null,
  staffId: null,
  facilityId: null,
  facilityBranchId: null,
  appointmentType: "", //virtual, in-person
  startDate: null,
  startTime: null,
  complain: "",
  channel: null,
  source: "riviaos",
};

const buildInitialState = (user) => ({
  ...appoinmentState,
  facilityId: user?.facility?.id || user?.facilityId || null,
  facilityBranchId: user?.branchId || null,
});

const NewAppointmentForm = ({ onSuccess: onDone, onExit }) => {
  const user = getUser();
  const [state, setState] = useState(() => buildInitialState(user));
  const [active, setActive] = useState(0);

  // stepper
  const finalStep = 2;
  const nextStep = () =>
    setActive((current) => (current < 3 ? current + 1 : current));
  const prevStep = () =>
    setActive((current) => (current > 0 ? current - 1 : current));

  const { Option } = Select;

  const { data: patients = [], refetch: fetchPatients } = useQuery({
    queryFn: getAllNetworkPatients,
    queryKey: ["patients"],
  });

  const {
    facilityOptions,
    branchesForFacility,
    doctors,
    selectedFacilityName,
    selectedBranchName,
    isChangingLocation,
    setIsChangingLocation,
    useCurrentLocation,
  } = useAppointmentLocation({ state, setState, user });

  const [modal, setModal] = useState({
    isOpen: false,
    title: "",
    content: null,
    size: "md",
  });

  const { mutateAsync: createAppointment, isLoading } = useMutation(
    (data) => postNewAppointment(data),
    {
      onSuccess: (data) => {
        if (data.status === 201) {
          message.success(data.data.message);
          onDone();
          setState(buildInitialState(user));
          setIsChangingLocation(false);
          setActive(0);
          return;
        }

        throw data;
      },
      onError: (error) => {
        const err = error?.response?.data?.message;
        if (_.isArray(err)) {
          err.map((err) => message.error(err?.message));
        } else {
          message.error(err);
        }
      },
    }
  );

  const handleSubmitAppointment = async () => {
    if (!state.facilityId) {
      message.error("Please select a facility");
      return;
    }

    if (!state.facilityBranchId) {
      message.error("Please select a facility location");
      return;
    }

    if (isPast(state.startDate)) {
      message.error("Date cannot be in the past");
      return;
    }

    if (isToday(state.startDate) && isPast(state.startTime)) {
      message.error("invalid time");
      return;
    }

    createAppointment(state);
  };

  return (
    <section>
      <Modal
        onClose={() => setModal({ ...modal, isOpen: false })}
        opened={modal.isOpen}
        title={modal.title}
        size={modal.size}>
        {modal.content}
      </Modal>
      <div className="row p-4">
        <div className="col-12 mt-1 mb-2">
          <Stepper
            active={active}
            onStepClick={setActive}
            breakpoint="sm"
            className="mb-4">
            <Stepper.Step
              label="Basic"
              description="requirements"
              allowStepSelect={active > 0}>
              <div className="row">
                <div className="col-12 mb-4">
                  <AppointmentLocationSection
                    state={state}
                    setState={setState}
                    facilityOptions={facilityOptions}
                    branchesForFacility={branchesForFacility}
                    selectedFacilityName={selectedFacilityName}
                    selectedBranchName={selectedBranchName}
                    isChangingLocation={isChangingLocation}
                    setIsChangingLocation={setIsChangingLocation}
                    useCurrentLocation={useCurrentLocation}
                  />
                </div>

                <div className="col-12 mb-4">
                  <div className="border rounded p-4">
                    <h6 className="app-text-primary">Member and Doctor</h6>
                    <div>
                      <label className="mb-2" htmlFor="patientId">
                        Select Patient
                      </label>
                      <div className="d-flex gap-2">
                        <Select
                          size="large"
                          required
                          allowClear
                          showSearch
                          autoFocus
                          id="patientId"
                          className="flex-grow-1"
                          placeholder="Search and select patient"
                          filterOption={(input, option) =>
                            option.children
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          filterSort={(optionA, optionB) =>
                            optionA.children
                              .toLowerCase()
                              .localeCompare(optionB.children.toLowerCase())
                          }
                          value={state.patientId}
                          onChange={(v) =>
                            dropDownSet("patientId", v, state, setState)
                          }>
                          {patients.map((dc) => (
                            <Option
                              value={dc.id}
                              key={
                                dc.id
                              }>{`${dc.firstName} ${dc.lastName} (${dc.contact})`}</Option>
                          ))}
                        </Select>
                        <button
                          type="button"
                          className="button is-light is-primary"
                          onClick={() =>
                            setModal({
                              title: "Add Patient",
                              isOpen: true,
                              content: (
                                <NewPatientForm
                                  onSuccess={fetchPatients}
                                  showCharm={false}
                                  showBottomButtons
                                />
                              ),
                              size: "lg",
                            })
                          }>
                          <span className="bi bi-plus-circle me-2"></span>
                          add
                        </button>
                      </div>
                      <small className="text-muted">
                        Select existing patient or add a new one
                      </small>
                    </div>
                    <div>
                      <label className="my-2" htmlFor="staffId">
                        Consulting Doctor
                      </label>
                      <Select
                        size="large"
                        allowClear
                        showSearch
                        value={state.staffId}
                        onChange={(v) =>
                          dropDownSet("staffId", v, state, setState)
                        }
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        filterSort={(optionA, optionB) =>
                          optionA.children
                            .toLowerCase()
                            .localeCompare(optionB.children.toLowerCase())
                        }
                        id="staffId"
                        className="w-100"
                        placeholder="Select doctor">
                        {doctors.map((dc) => (
                          <Option
                            value={dc.id}
                            key={
                              dc.id
                            }>{`${dc.firstName} ${dc.lastName}`}</Option>
                        ))}
                      </Select>
                      <small className="text-muted">
                        Who will be meeting{" "}
                        {patients.find((p) => p.id === state.patientId)
                          ?.firstName || " the patient"}
                        ?
                      </small>
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <div className="border rounded p-4">
                    <h6 className="app-text-primary">Complaint</h6>
                    <label className="mb-2" htmlFor="complain">
                      Describe the issue
                    </label>
                    <textarea
                      className="textarea"
                      style={{ minHeight: "100px", resize: "vertical" }}
                      value={state.complain}
                      onChange={(e) =>
                        dropDownSet("complain", e.target.value, state, setState)
                      }
                      required
                      id="complain"
                      placeholder="Describe the patient's symptoms or reason for visit..."></textarea>
                    <small className="text-muted">
                      Please provide details about the patient's condition
                    </small>
                  </div>
                </div>
              </div>
            </Stepper.Step>
            <Stepper.Step
              label="Schedule"
              description="appointment"
              allowStepSelect={active > 1}>
              <div className="row">
                {/* Appointment Type Group */}
                <div className="col-12 mb-4">
                  <div className="border rounded p-4">
                    <h6 className="app-text-primary m-0">Appointment Type</h6>
                    <small className="p-0">
                      What type of appointment is this?
                    </small>
                    <div className="row g-3 mt-1">
                      <div className="col-md-6">
                        <button
                          className={`w-100 app-btn ${
                            state.appointmentType === "virtual" && "btn-prim"
                          } px-5 py-2`}
                          style={{
                            border: "1px solid #dbdbdb",
                            borderRadius: "8px",
                            transition: "all 0.2s ease",
                            backgroundColor:
                              state.appointmentType === "virtual"
                                ? "var(--bs-primary)"
                                : "transparent",
                            color:
                              state.appointmentType === "virtual"
                                ? "white"
                                : "inherit",
                          }}
                          onClick={() =>
                            setState({
                              ...state,
                              appointmentType: "virtual",
                              channel: state.channel || "video",
                            })
                          }>
                          <IconVideo size={32} />
                          <p className="mt-2 mb-0 fw-semibold">Virtual</p>
                          <small>Video call appointment</small>
                        </button>
                      </div>
                      <div className="col-md-6">
                        <button
                          className={`w-100 app-btn ${
                            state.appointmentType === "in-person" && "btn-prim"
                          } px-5 py-2`}
                          style={{
                            border: "1px solid #dbdbdb",
                            borderRadius: "8px",
                            transition: "all 0.2s ease",
                            backgroundColor:
                              state.appointmentType === "in-person"
                                ? "var(--bs-primary)"
                                : "transparent",
                            color:
                              state.appointmentType === "in-person"
                                ? "white"
                                : "inherit",
                          }}
                          onClick={() =>
                            setState({
                              ...state,
                              appointmentType: "in-person",
                              channel: null,
                            })
                          }>
                          <IconUser size={32} />
                          <p className="mt-2 mb-0 fw-semibold">In-Person</p>
                          <small>Physical office visit</small>
                        </button>
                      </div>
                    </div>
                    {state.appointmentType === "virtual" && (
                      <div className="mt-3">
                        <label className="mb-2">Virtual Channel</label>
                        <Select
                          size="large"
                          value={state.channel}
                          onChange={(v) =>
                            dropDownSet("channel", v, state, setState)
                          }
                          className="w-100"
                          placeholder="Select channel">
                          <Option value="video">Video</Option>
                          <Option value="call">Call</Option>
                          <Option value="chat">Chat</Option>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>

                {/* Schedule Group */}
                <div className="col-12">
                  <div className="border rounded p-4">
                    <h6 className="app-text-primary m-0">Schedule Details</h6>
                    <small className="p-0">
                      Set the schedule (start date and time)?
                    </small>
                    <div className="row g-3 mt-1">
                      <div className="col-md-6">
                        <label className="mb-2">
                          <span className="bi bi-calendar-event me-2"></span>
                          Date
                        </label>
                        <DatePicker
                          onChange={(data) => {
                            dropDownSet("startDate", data, state, setState);
                          }}
                          id="startDate"
                          size="large"
                          value={state.startDate}
                          format={dateFormat}
                          className="w-100"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="mb-2">
                          <span className="bi bi-clock me-2"></span>
                          Time
                        </label>
                        <TimePicker
                          size="large"
                          allowClear
                          showSearch
                          minuteStep={1}
                          hideDisabledOptions={false}
                          value={state.startTime}
                          onChange={(time) => {
                            dropDownSet("startTime", time, state, setState);
                          }}
                          placeholder="Select time"
                          className="w-100"
                          format="h:mm a"
                          presets={[
                            {
                              label: "Morning",
                              values: ["06:00:00", "08:00:00", "10:00:00"],
                            },
                            {
                              label: "Afternoon",
                              values: ["12:00:00", "14:00:00", "16:00:00"],
                            },
                            {
                              label: "Evening",
                              values: ["18:00:00", "20:00:00", "22:00:00"],
                            },
                          ]}
                        />
                      </div>
                    </div>
                    <small className="text-muted">
                      Select the date and time for the appointment
                    </small>
                  </div>
                </div>
              </div>
            </Stepper.Step>
            <Stepper.Step
              label="Confirm"
              description="check & submit"
              allowStepSelect={active > 2}>
              <div className="row mt-4">
                <div className="col-12">
                  <div className="text-center mb-4">
                    <div className="d-flex align-items-center justify-content-center mb-3">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: 50,
                          height: 50,
                          background: "#e0e7ff",
                          borderRadius: "50%",
                        }}>
                        <span
                          className={`bi ${
                            state.appointmentType === "virtual"
                              ? "bi-camera-video"
                              : "bi-person-video2"
                          } mb-0 fs-4`}></span>
                      </div>
                      <div>
                        <h6 className="mb-1">
                          {_.capitalize(state.appointmentType)} Appointment
                        </h6>
                        {state.appointmentType === "virtual" && state.channel && (
                          <small className="text-muted text-capitalize d-block">
                            Channel: {state.channel}
                          </small>
                        )}
                        <div className="d-flex align-items-center">
                          <p className="m-0 me-2">for</p>
                          <Badge color="blue" size="lg" className="mt-0">
                            {doctors?.find((d) => d.id === state.staffId)
                              ?.firstName || "Doctor"}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Divider className="my-4" />

                  <div className="mb-3">
                    <p className="mb-1 text-muted small">Where</p>
                    <p className="mb-0 fw-semibold">{selectedFacilityName}</p>
                  </div>

                  <div className="row g-4">
                    <div className="col-md-6">
                      <div className="border rounded p-4 h-100">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <p className="mb-0">
                            <span className="bi bi-person me-2"></span>
                            Member Details
                          </p>
                          <span
                            className="hover-hand"
                            title="View Patient Details"
                            onClick={() =>
                              setModal({
                                title: "Patient Details",
                                isOpen: true,
                                content: (
                                  <EditPatientForm
                                    patientId={state.patientId}
                                    isEditable={false}
                                    showCharm={false}
                                  />
                                ),
                              })
                            }>
                            <span className="bi bi-arrow-up-right-square"></span>
                          </span>
                        </div>
                        <p className="mb-1">
                          {patients?.find((p) => p.id === state.patientId)
                            ?.firstName || ""}{" "}
                          {patients?.find((p) => p.id === state.patientId)
                            ?.lastName || ""}
                        </p>
                        <div className="text-muted">
                          <div className="mb-1">
                            <span className="bi bi-telephone me-2"></span>
                            {
                              patients?.find((p) => p.id === state.patientId)
                                ?.contact
                            }
                          </div>
                          {/* <div>
                            <span className="bi bi-gender-ambiguous me-2"></span>
                            {
                              patients?.find((p) => p.id === state.patientId)
                                ?.gender
                            }
                          </div> */}
                        </div>
                      </div>
                    </div>

                    {/* Schedule Details Card */}
                    <div className="col-md-6">
                      <div className="border rounded p-4 h-100">
                        <p className="mb-3">
                          <span className="bi bi-calendar-event me-2"></span>
                          Schedule Details
                        </p>
                        <div className="mb-1 d-flex align-items-center">
                          {/* <strong className="me-2">Date:</strong> */}
                          <div className="text-muted">
                            {isToday(new Date(state.startDate))
                              ? "Today"
                              : format(
                                  new Date(state.startDate),
                                  "EEE, MMM d, yyyy"
                                )}
                          </div>
                        </div>
                        <div className="mb-0 d-flex align-items-center">
                          {/* <strong className="me-2">Time:</strong> */}
                          <div className="text-muted">
                            {state.startTime &&
                              new Date(state.startTime).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Divider className="my-4" />

                  <div className="text-center">
                    <p className="mb-0 fw-semibold">
                      Ready to confirm this appointment?
                      <span className="bi bi-patch-check-fill text-success ms-2"></span>
                    </p>
                    <small className="text-muted">
                      Please review all details above and click the Submit
                      button to confirm.
                    </small>
                  </div>
                </div>
              </div>
            </Stepper.Step>
            <Stepper.Completed>
              <div className="mb-0 text-success text-center mt-4">
                <span className="h1 bi bi-patch-check-fill"></span>
                <h1 className="mt-0">Submitted !</h1>
              </div>
            </Stepper.Completed>
          </Stepper>

          <Group position="center" mt="xl" className="mt-4">
            {active > 0 && !isLoading && (
              <button className="button is-light" onClick={prevStep}>
                <span className="bi bi-arrow-left" />
                <span className="ms-2">Back</span>
              </button>
            )}
            {active < finalStep && !isLoading && (
              <button className="button is-light" onClick={nextStep}>
                <span className="me-2">Next</span>
                <span className="bi bi-arrow-right" />
              </button>
            )}

            {active > finalStep && !isLoading && (
              <button className="button is-light" onClick={onExit}>
                <span className="bi bi-check" />
                <span className="ms-2">Done</span>
              </button>
            )}
            {active === finalStep && (
              <button
                onClick={() => handleSubmitAppointment(state)}
                className={`button is-primary ${isLoading && " is-loading"}`}>
                <span className="me-2">Submit</span>
              </button>
            )}
          </Group>
        </div>
      </div>
    </section>
  );
};

export default NewAppointmentForm;
